import { required } from '@vuelidate/validators'
import { isValidEmailAddress } from '@/validations/validators'
import router from '@/router'
import type { Config } from './tour'
import { useDevStore } from '@/stores/dev/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useCtaStore } from '@/stores/cta/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { computed } from 'vue'
import { i18n } from '@/core/i18n'
import { useExtensionStore } from '@/stores/extension/store'

/*
  Requirements:
    - At least 1 extension of type “call”, “IVR” or “queue”
    - Classic or Pro edition, not Basic
  Steps:
    - Select the extension to activate the night switch of
    - Set the contact settings in this extension (at least email)
    - Click on edit Night switch
      - Check the night switch active checkbox
      - Select audio file (voice mail message)
      - Select notification type (when caller leaves a voice mail)
      - Select notification channel (email)
      - Save
    - Show the activate night switch checkbox in the config (keep it active)
    - Save config (back to overview)
    - Click on the moon to open the night switch settings
      - Deactivate it
      - Save night switch config
      - IMPORTANT: save the extension config
*/

export const CTANightSwitchSetupConfig: Config = {
  onStart() {
    // Redirect to overview
    router.push('/').catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        throw error
      }
    })

    // Force re-render to reset the view (if in edit extensions mode, the add button is hidden)
    useDevStore().forceRerender()
  },
  steps: [
    // Show requirements
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.nightSwitchSetup.intro.title',
      text: 'tutorial.cta.nightSwitchSetup.intro.text',
      showNextButton: true,
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      canGoToNext() {
        // Check if is not Basic edition

        const edition = useCtaStore().getEdition

        const isBasicEdition = edition === 'basic'

        if (isBasicEdition) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.nightSwitchSetup.intro.requirements.basicEdition.invalid'
              )
            ),
          })

          return false
        }

        // Check has at least 1 extension already set up

        const extensions = useExtensionsStore().list

        const extensionsThatCanHaveNightSwitch = extensions.filter((e) =>
          ['ivr', 'default', 'queue'].includes(e.type)
        )

        const hasExtensions = extensionsThatCanHaveNightSwitch.length

        if (!hasExtensions) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.nightSwitchSetup.intro.requirements.hasExtensionOfTypeCallIvrOrQueue.invalid'
              )
            ),
          })

          return false
        }

        return true
      },
    },
    // Select extension to add the night switch
    {
      title: 'tutorial.cta.nightSwitchSetup.selectExtension.title',
      text: 'tutorial.cta.nightSwitchSetup.selectExtension.text',
      attachElement: '[data-test=overview-section-extension]',
      nextOnClickElements: [
        ':is([data-extension-type=ivr],[data-extension-type=default],[data-extension-type=queue]) [data-test=edit-extension]',
      ],
      highlightElements: [
        ':is([data-extension-type=ivr],[data-extension-type=default],[data-extension-type=queue]) [data-test=edit-extension]',
      ],
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-extension-type]:not([data-extension-type=ivr],[data-extension-type=default],[data-extension-type=queue]) [data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-night-switch]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
      attachPosition: 'top',
    },
    // Extension: add a contact email
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.email.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.email.text',
      attachElement: '[data-test=extension-email]',
      preventEnterAndTabInElements: ['[data-test=extension-email]'],
      watchValueOfElements: ['[data-test=extension-email]'],
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=extension-email]'
        )

        const value = element?.value

        if (!required.$validator(value, undefined, undefined)) {
          return false
        }

        if (!isValidEmailAddress(value ?? '')) {
          return false
        }

        return true
      },
    },
    // Extension: edit night switch
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.editNightSwitch.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.editNightSwitch.text',
      attachElement: '[data-test=nightswitch-btn]',
      nextOnClickElements: ['[data-test=nightswitch-btn]'],
      attachPosition: 'top',
    },
    // Extension: edit night switch: save other changes
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.saveChanges.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.saveChanges.text',
      attachElement: '[data-test=modal-primary-button]',
      nextOnClickElements: ['[data-test=modal-primary-button]'],
      attachPosition: 'top',
      showOn() {
        const originalEmail = useExtensionStore().get?.email

        const emailInput = document.querySelector<HTMLInputElement>(
          '[data-test=extension-email]'
        )

        const newEmail = emailInput?.value

        return newEmail !== originalEmail
      },
    },
    // Extension: edit night switch: Activate the night switch
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.activate.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.activate.text',
      attachElement: '[data-test=activate-night-switch-checkbox]',
      preventEnterAndTabInElements: ['#is-closing-active'],
      watchValueOfElements: ['#is-closing-active'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element =
          document.querySelector<HTMLInputElement>('#is-closing-active')

        const checked = element?.checked

        // Check that the night switch is activated
        return checked === true
      },
    },
    // Extension: edit night switch: Select an audio file
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.audioFile.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.audioFile.text',
      attachElement: '[data-test=prompt-id]',
      preventEnterAndTabInElements: ['[data-test=prompt-id]'],
      watchValueOfElements: ['[data-test=prompt-id]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=prompt-id]'
        )

        const value = element?.value

        // Check that the night switch is set to standard voice mail
        return value === '2'
      },
    },
    // Extension: edit night switch: Select notification type
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.notificationType.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.notificationType.text',
      attachElement: '[data-test=notification-type]',
      preventEnterAndTabInElements: ['[data-test=notification-type]'],
      watchValueOfElements: ['[data-test=notification-type]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=notification-type]'
        )

        const value = element?.value

        // Check that the notification is set to when the caller has left a message
        return value === 'leftMessage'
      },
    },
    // Extension: edit night switch: Select notification channel
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.notificationChannel.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.notificationChannel.text',
      attachElement: '[data-test=notification-channel]',
      preventEnterAndTabInElements: ['[data-test=notification-channel]'],
      watchValueOfElements: ['[data-test=notification-channel]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=notification-channel]'
        )

        const value = element?.value

        // Check that the notification is set to when the caller has left a message
        return value === 'email'
      },
    },
    // Extension: edit night switch: Save night switch
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.save.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Extension: edit night switch: show the activate night switch checkbox
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.activateNightSwitchCheckbox.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.activateNightSwitchCheckbox.text',
      attachElement: 'label[for=isClosing]',
      preventEnterAndTabInElements: ['#isClosing'],
      watchValueOfElements: ['#isClosing'],
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>('#isClosing')

        const checked = element?.checked

        // Check that the night switch is activated
        return checked === true
      },
    },
    // Extension: Save extension
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.save.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Open edit night switch from the Overview page with the Moon icon
    {
      title: 'tutorial.cta.nightSwitchSetup.openEditNightSwitch.title',
      text: 'tutorial.cta.nightSwitchSetup.openEditNightSwitch.text',
      attachElement: '[data-test=overview-section-extension]',
      nextOnClickElements: ['[data-test=edit-night-switch]'],
      highlightElements: ['[data-test=edit-night-switch]'],
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
      attachPosition: 'top',
    },
    // Extension: edit night switch: Deactivate the night switch
    {
      title:
        'tutorial.cta.nightSwitchSetup.extension.nightSwitch.deactivate.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch.deactivate.text',
      attachElement: '[data-test=activate-night-switch-checkbox]',
      preventEnterAndTabInElements: ['#is-closing-active'],
      watchValueOfElements: ['#is-closing-active'],
      attachPosition: 'top',
      showNextButton: true,
      canGoToNext() {
        const element =
          document.querySelector<HTMLInputElement>('#is-closing-active')

        const checked = element?.checked

        // Check that the night switch is activated
        return checked === false
      },
    },
    // Extension: edit night switch: Save night switch
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch2.save.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.nightSwitch2.save.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
    },
    // Extension: Save extension
    {
      title: 'tutorial.cta.nightSwitchSetup.extension.save2.title',
      text: 'tutorial.cta.nightSwitchSetup.extension.save2.text',
      attachElement: '[data-test=action-btn-save]',
      attachPosition: 'top',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
    },
    // Overview: show end message
    {
      title: 'tutorial.cta.nightSwitchSetup.end.title',
      text: 'tutorial.cta.nightSwitchSetup.end.text',
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      showFinishButton: true,
    },
  ],
}
